/**
 * One sorting model for every table in Atlas.
 *
 * The house rule, and it holds for the screens already built and for every
 * screen still to come: **every column a table renders is sortable, except a
 * column that holds row actions.** A column that sorts on one screen and not
 * on the next is a defect the reader has to discover one table at a time.
 *
 * Everything here is pure and framework-free, so the ordering rules are
 * testable without rendering anything. A screen supplies one function saying
 * what a column's value IS for a row; this module decides how values compare
 * and which way round they go.
 */

/** Which way a column is ordered. */
export type SortDirection = 'asc' | 'desc';

export interface SortState<Column extends string> {
  readonly column: Column;
  readonly direction: SortDirection;
}

/**
 * What a cell sorts by. Not what it renders: a status cell renders a badge
 * and sorts by its label, a date cell renders "20 Sep 2026" and sorts by the
 * instant behind it.
 */
export type SortValue = string | number | boolean | Date | null | undefined;

/**
 * Clicking a header: the same column flips direction, a new column starts
 * ascending. The one interaction every table shares.
 */
export function nextSort<Column extends string>(
  current: SortState<Column>,
  column: Column,
): SortState<Column> {
  if (current.column !== column) return { column, direction: 'asc' };
  return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

/**
 * Compare two cell values.
 *
 * Empty sorts LAST in both directions. A row with no owner or no sign-in is
 * an absence, and an absence that floats to the top of a descending sort
 * buries the rows the reader asked to see.
 */
export function compareValues(left: SortValue, right: SortValue): number {
  const leftEmpty = left === null || left === undefined || left === '';
  const rightEmpty = right === null || right === undefined || right === '';
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;

  if (left instanceof Date || right instanceof Date) {
    return Number(new Date(left as Date)) - Number(new Date(right as Date));
  }
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }
  // Accent- and case-insensitive, so "Różanek" files with "Rozanek" and a
  // capital does not jump a name to the front of the list.
  return String(left).localeCompare(String(right), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
}

/**
 * A sorted copy. Never sorts in place: the array a query hook returns is
 * cached, and reordering it under React is how a list starts reshuffling on
 * an unrelated re-render.
 *
 * The sort is stable, so rows equal on the chosen column keep the order the
 * server sent them in.
 */
export function sortRows<Row, Column extends string>(
  rows: readonly Row[],
  sort: SortState<Column>,
  valueOf: (row: Row, column: Column) => SortValue,
): Row[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort(
    (left, right) =>
      direction * compareValues(valueOf(left, sort.column), valueOf(right, sort.column)),
  );
}

/** What a screen reader is told a header button will do. */
export function sortAriaLabel(label: string, active: boolean, direction: SortDirection) {
  if (!active) return `Sort by ${label}`;
  return `Sorted by ${label}, ${direction === 'asc' ? 'ascending' : 'descending'}. Reverse the order`;
}
